import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import { cardFactory } from '@liveauctioneers/hammer-ui-core/card/factory';
import { useCallback, useState } from 'react';

import { ItemCardPriceRow } from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';
import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryCatalogId, getItemSummaryPromoted } from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemSummary } from '@/types/item/ItemSummary';
import {
    ItemCardSource,
    useTrackItemCardClicked,
} from '@/utils/amplitude/analytics/item/trackItemCardClicked.analytics';
import {
    LotRecommendationTrigger,
    LotRecommendationType,
} from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';

import { ImageComponent } from './components/ImageComponent';
import { TitleComponent } from './components/TitleComponent';

export type ItemPageItemCardProps = ItemSummary & {
    itemCardSource?: ItemCardSource;
    position?: number;
    recommendationType?: LotRecommendationType;
};

export const ItemPageItemCard = cardFactory<ItemPageItemCardProps>({
    components: [
        ImageComponent,
        { spacerPx: 8 },
        createInfo({
            className: 'line-clamp-0',
            skeletonWidth: 100,
            useText: ({ itemId }) => {
                return (
                    <ItemCardShortDate
                        allowBidding
                        itemId={itemId}
                    />
                );
            },
        }),
        { spacerPx: 2 },
        TitleComponent,
        { spacerPx: 8 },
        createInfo({
            className: 'line-clamp-0',
            skeletonWidth: 200,
            useText: ({
                bidCount,
                itemCardSource = ItemCardSource.LotDetailsPage,
                itemId,
                position,
                recommendationType,
            }) => {
                const [linkHover, setLinkHover] = useState(false);

                const trackItemCardClicked = useTrackItemCardClicked();
                const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
                const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
                const onRecommendationClick = useRecommendationTracking({
                    isZeroBasedPosition: true,
                    itemId,
                    recommendationPosition: position ?? 0,
                    recommendationType,
                });

                const handleClick = useCallback(
                    (trigger: LotRecommendationTrigger) => {
                        onRecommendationClick(trigger);
                        trackItemCardClicked({
                            catalogId,
                            itemId,
                            promoted,
                            source: itemCardSource,
                        });
                    },
                    [catalogId, itemCardSource, itemId, promoted, trackItemCardClicked, onRecommendationClick]
                );

                const enterLinkHoverState = useCallback(() => {
                    setLinkHover(true);
                }, []);

                const leaveLinkHoverState = useCallback(() => {
                    setLinkHover(false);
                }, []);

                return (
                    <ItemCardPriceRow
                        isHovered={linkHover}
                        itemId={itemId}
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                        onNavigate={() => {
                            handleClick(LotRecommendationTrigger.TitleClick);
                        }}
                        showBidCount={Boolean(bidCount)}
                    />
                );
            },
        }),
    ],
    name: 'ItemPageItemCard',

    overrideSkeletonHeightPx: 400,
});
